/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { HttpClient, HttpErrorResponse, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class EngineAttributesService {

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  private readonly baseURL = this.configService.baseApiUrl + 'manage-attributes/engine-attribute';

  /**
   * Return data for selected attribute.
   * @param selectedAttribute get data for the dropdown.
   * @param engineFamily selected value for engineFamily dropdown.
   * @param engineRPO selected value for engine Rpo dropdown.
   * @param batrpo selected value for batrop dropdown.
   * @param boost selected value for boost dropdown.
   * @param engineCam selected value for engineCam.
   * @param engineDsp selected value for engineDsp dropdown.
   * @param engineCyl selected value for engineCyl dropdown.
   * @param enginePower selected value for enginePower dropdown.
   * @param engineTorque selected value for engineTorque dropdown.
   * @param vfuel selected value for vFuel dropdown.
   * @param injection selected value for injection dropdown.
   */
  getFilterOptions(selectedAttribute, engineFamily, engineRPO, batrpo,
                   boost, engineCam, engineDsp, engineCyl,
                   enginePower, engineTorque, vfuel, injection): Observable<any> {
    const url = this.baseURL + '/get-filter-option';
    return this.http.post<any>(url,
      {
        selectedAttribute, engineFamily, engineRPO, batrpo,
        boost, engineCam, engineDsp, engineCyl,
        enginePower, engineTorque, vfuel, injection
      })
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Return data that will be updated if a change in value has been committed.
   * @param selectedAttribute get data for the dropdown.
   * @param selectedValue selected value for current dropdown.
   * @param newValue user entered new value to replace selectedValue.
   * @param engineFamily selected value for engineFamily dropdown.
   * @param engineRPO selected value for engine Rpo dropdown.
   * @param engineInfo selected value for batrop dropdown.
   * @param boost selected value for boost dropdown.
   * @param engineCam selected value for engineCam.
   * @param engineDsp selected value for engineDsp dropdown.
   * @param engineCyl selected value for engineCyl dropdown.
   * @param enginePower selected value for enginePower dropdown.
   * @param engineTorque selected value for engineTorque dropdown.
   * @param vfuel selected value for vFuel dropdown.
   * @param injection selected value for injection dropdown.
   */
  getPreviewChanges(selectedAttribute, selectedValue, newValue, engineFamily, engineRPO,
                    engineInfo, boost, engineCam, engineDsp, engineCyl,
                    enginePower, engineTorque, vfuel, injection): Observable<any> {
    const headers = new HttpHeaders();
    headers['Content-Disposition'] = 'attachment; filename=ChangeAttribute.xlsx';
    const url = this.baseURL + '/preview-changes';
    return this.http.post(url, {
      selectedAttribute, selectedValue, newValue, engineFamily, engineRPO,
      engineInfo, boost, engineCam, engineDsp, engineCyl,
      enginePower, engineTorque, vfuel, injection
    }, {headers, responseType: 'blob'})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Return data with old and new values.
   * @param selectedAttribute get data for the dropdown.
   * @param selectedValue selected value for current dropdown.
   * @param newValue user entered new value to replace selectedValue.
   * @param engineFamily selected value for engineFamily dropdown.
   * @param engineRPO selected value for engine Rpo dropdown.
   * @param engineInfo selected value for batrop dropdown.
   * @param boost selected value for boost dropdown.
   * @param engineCam selected value for engineCam.
   * @param engineDsp selected value for engineDsp dropdown.
   * @param engineCyl selected value for engineCyl dropdown.
   * @param enginePower selected value for enginePower dropdown.
   * @param engineTorque selected value for engineTorque dropdown.
   * @param vfuel selected value for vFuel dropdown.
   * @param injection selected value for injection dropdown.
   */
  getCommitChanges(selectedAttribute, selectedValue, newValue, engineFamily, engineRPO,
                   engineInfo, boost, engineCam, engineDsp, engineCyl,
                   enginePower, engineTorque, vfuel, injection): Observable<any> {
    const headers = new HttpHeaders();
    headers['Content-Disposition'] = 'attachment; filename=ChangeAttribute.xlsx';
    const url = this.baseURL + '/commit-changes';
    return this.http.post(url, {
      selectedAttribute, selectedValue, newValue, engineFamily, engineRPO,
      engineInfo, boost, engineCam, engineDsp, engineCyl,
      enginePower, engineTorque, vfuel, injection
    }, {headers, responseType: 'blob'})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred';
    switch (errorResponse.status) {
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error && errorResponse.error.message) {
          errorMessage = errorResponse.error.message;
        } else {
          errorMessage = 'Error getting data.';
        }
        break;
      }
    }
    return throwError(errorMessage);
  }

}
